import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { useApp } from "../contexts/AppContext";
import { useAuth } from "../contexts/AuthContext";
import { logSearch } from "../services/analytics";
import { Search, SlidersHorizontal, X, ChevronDown, ChevronRight } from "lucide-react";

/**
 * CatalogPage — top-filter layout.
 * ─ Filters (search / price range / categories accordion / artists collapsible / sort)
 *   are placed at the TOP of the page (not left sidebar).
 * ─ Categories are rendered as a nested tree; clicking the row expands its
 *   children (accordion). Clicking the label also filters by that category.
 * ─ Mobile: the whole filter block collapses under a "Filterlər" toggle.
 */
export default function CatalogPage() {
  const { t, tp } = useApp();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();

  const initialQ = params.get("q") || "";
  const initialCat = params.get("category") || "";
  const initialArt = params.get("artist") || "";
  const initialMin = params.get("min") || "";
  const initialMax = params.get("max") || "";
  const initialSort = params.get("sort") || "newest";

  const [q, setQ] = useState(initialQ);
  const [products, setProducts] = useState([]);
  const [artists, setArtists] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState(initialCat);
  const [activeArt, setActiveArt] = useState(initialArt);
  const [minPrice, setMinPrice] = useState(initialMin);
  const [maxPrice, setMaxPrice] = useState(initialMax);
  const [sort, setSort] = useState(initialSort);

  // UI state
  const [filtersOpen, setFiltersOpen] = useState(false); // mobile drawer toggle
  const [expandedCats, setExpandedCats] = useState({}); // cat id → bool
  const [artistsExpanded, setArtistsExpanded] = useState(false);
  const [artistSearch, setArtistSearch] = useState("");

  useEffect(() => {
    api.get("/artists").then((r) => setArtists(r.data || [])).catch(() => setArtists([]));
    api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => setCategories([]));
  }, []);

  const fetchProducts = (qStr, catSlug, artSlug) => {
    const qs = new URLSearchParams();
    if (qStr) qs.set("q", qStr);
    if (catSlug) {
      const c = categories.find((x) => x.slug === catSlug);
      if (c) qs.set("category_id", c.id);
    }
    if (artSlug) {
      const a = artists.find((x) => x.slug === artSlug);
      if (a) qs.set("artist_id", a.id);
    }
    api.get(`/products?${qs}`).then((r) => setProducts(r.data || [])).catch(() => setProducts([]));
  };

  useEffect(() => {
    if (categories.length === 0 && artists.length === 0) {
      api.get("/products").then((r) => setProducts(r.data || [])).catch(() => setProducts([]));
    } else {
      fetchProducts(q, activeCat, activeArt);
    }
  }, [categories, artists]);

  // Auto-expand the ancestors of the active category so it's visible on load
  useEffect(() => {
    if (!activeCat || categories.length === 0) return;
    const bySlug = Object.fromEntries(categories.map((c) => [c.slug, c]));
    const byId = Object.fromEntries(categories.map((c) => [c.id, c]));
    const walk = {};
    let cur = bySlug[activeCat];
    let guard = 0;
    while (cur && cur.parent_id && guard++ < 10) {
      walk[cur.parent_id] = true;
      cur = byId[cur.parent_id];
    }
    setExpandedCats((s) => ({ ...s, ...walk }));
  }, [activeCat, categories]);

  const applyFilters = (patch = {}) => {
    const next = {
      q: patch.q ?? q,
      category: patch.category ?? activeCat,
      artist: patch.artist ?? activeArt,
      min: patch.min ?? minPrice,
      max: patch.max ?? maxPrice,
      sort: patch.sort ?? sort,
    };
    const paramsObj = {};
    Object.entries(next).forEach(([k, v]) => { if (v) paramsObj[k] = v; });
    setParams(paramsObj);
    fetchProducts(next.q, next.category, next.artist);
    if (patch.q && next.q) logSearch(next.q, user?.uid);
  };

  const onSearch = (e) => {
    e.preventDefault();
    applyFilters({ q });
  };

  const pickCategory = (slug) => {
    const nextSlug = activeCat === slug ? "" : slug;
    setActiveCat(nextSlug);
    applyFilters({ category: nextSlug });
  };

  const pickArtist = (slug) => {
    const nextSlug = activeArt === slug ? "" : slug;
    setActiveArt(nextSlug);
    applyFilters({ artist: nextSlug });
  };

  const toggleCatExpand = (id) => setExpandedCats((s) => ({ ...s, [id]: !s[id] }));

  const clearAll = () => {
    setQ("");
    setActiveCat("");
    setActiveArt("");
    setMinPrice("");
    setMaxPrice("");
    setSort("newest");
    setArtistSearch("");
    setParams({});
    api.get("/products").then((r) => setProducts(r.data || []));
  };

  const artistById = useMemo(
    () => Object.fromEntries(artists.map((a) => [a.id, a])),
    [artists]
  );

  // Build category tree from flat list
  const categoryTree = useMemo(() => {
    const byParent = {};
    for (const c of categories) {
      const p = c.parent_id || "_root";
      (byParent[p] = byParent[p] || []).push(c);
    }
    const attach = (c) => ({ ...c, children: (byParent[c.id] || []).map(attach) });
    return (byParent._root || []).map(attach);
  }, [categories]);

  const filteredArtists = useMemo(() => {
    if (!artistSearch.trim()) return artists;
    const s = artistSearch.toLowerCase();
    return artists.filter((a) => (a.name || "").toLowerCase().includes(s));
  }, [artists, artistSearch]);

  // Compute visible products after client-side price filter + sort
  const visibleProducts = useMemo(() => {
    let list = [...products];
    const mn = parseFloat(minPrice);
    const mx = parseFloat(maxPrice);
    if (!isNaN(mn)) list = list.filter((p) => Number(p.price_azn || 0) >= mn);
    if (!isNaN(mx)) list = list.filter((p) => Number(p.price_azn || 0) <= mx);
    if (sort === "price_asc") list.sort((a, b) => Number(a.price_azn || 0) - Number(b.price_azn || 0));
    else if (sort === "price_desc") list.sort((a, b) => Number(b.price_azn || 0) - Number(a.price_azn || 0));
    else if (sort === "title_asc") list.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    // newest = whatever order backend returned
    return list;
  }, [products, minPrice, maxPrice, sort]);

  const activeChipsCount =
    (q ? 1 : 0) +
    (activeCat ? 1 : 0) +
    (activeArt ? 1 : 0) +
    (minPrice || maxPrice ? 1 : 0);

  const renderFilterBlock = () => (
    <div className="space-y-6">
      {/* Search */}
      <div>
        <div className="text-[10px] tracking-[0.16em] uppercase text-[var(--text-muted)] mb-2 font-medium">
          {t.common.search}
        </div>
        <form onSubmit={onSearch} className="flex border border-[var(--border)] bg-[var(--bg)]">
          <input
            type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={t.common.search}
            className="bg-transparent flex-1 px-3 py-2 outline-none text-sm min-w-0"
            data-testid="catalog-search-input"
          />
          <button
            type="submit"
            className="px-3 border-l border-[var(--border)] hover:bg-[var(--surface)] text-[var(--text-2)]"
            data-testid="catalog-search-btn"
          >
            <Search size={14} />
          </button>
        </form>
      </div>

      {/* Price range */}
      <div>
        <div className="text-[10px] tracking-[0.16em] uppercase text-[var(--text-muted)] mb-2 font-medium">
          {t.catalog?.priceRange || "Qiymət aralığı (AZN)"}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number" min="0" inputMode="numeric"
            value={minPrice}
            onChange={(e) => setMinPrice(e.target.value)}
            onBlur={() => applyFilters({ min: minPrice })}
            placeholder="Min"
            className="w-full bg-[var(--bg)] border border-[var(--border)] px-3 py-2 outline-none text-sm focus:border-[var(--accent)]"
            data-testid="filter-price-min"
          />
          <span className="text-[var(--text-muted)] text-sm">—</span>
          <input
            type="number" min="0" inputMode="numeric"
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
            onBlur={() => applyFilters({ max: maxPrice })}
            placeholder="Max"
            className="w-full bg-[var(--bg)] border border-[var(--border)] px-3 py-2 outline-none text-sm focus:border-[var(--accent)]"
            data-testid="filter-price-max"
          />
        </div>
      </div>

      {/* Categories tree */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] tracking-[0.16em] uppercase text-[var(--text-muted)] font-medium">
            {t.catalog?.categoriesFilter || "Kateqoriyalar"}
          </div>
          {activeCat && (
            <button
              type="button"
              onClick={() => pickCategory(activeCat)}
              className="text-[10px] tracking-[0.14em] uppercase text-[var(--accent)] hover:underline"
              data-testid="filter-cat-clear"
            >
              {t.common.clear || "Sıfırla"}
            </button>
          )}
        </div>
        <ul className="border border-[var(--border)] bg-[var(--bg)] max-h-72 overflow-y-auto divide-y divide-[var(--border)]/60">
          <li>
            <button
              type="button"
              onClick={() => { setActiveCat(""); applyFilters({ category: "" }); }}
              className={`w-full text-left px-3 py-2 text-sm ${
                activeCat === "" ? "text-[var(--accent)] font-medium" : "text-[var(--text-2)] hover:text-[var(--text)]"
              }`}
              data-testid="filter-cat-all"
            >
              {t.catalog?.all || "Hamısı"}
            </button>
          </li>
          <li>
            <ul className="pl-1 pr-1">
              {categoryTree.map((c) => renderCategoryNode({ node: c, depth: 0, activeCat, expandedCats, pickCategory, toggleCatExpand, setExpandedCats }))}
              {categoryTree.length === 0 && (
                <li className="px-3 py-2 text-[11px] text-[var(--text-muted)]">…</li>
              )}
            </ul>
          </li>
        </ul>
      </div>

      {/* Artists — collapsed by default */}
      <div>
        <button
          type="button"
          onClick={() => setArtistsExpanded((v) => !v)}
          className="w-full flex items-center justify-between mb-2 text-[10px] tracking-[0.16em] uppercase text-[var(--text-muted)] font-medium hover:text-[var(--text)]"
          data-testid="filter-artists-toggle"
        >
          <span>
            {t.catalog?.artistsFilter || "Sənətçilər"}
            {activeArt && (
              <span className="ml-2 text-[var(--accent)] normal-case tracking-normal">
                · {artists.find((a) => a.slug === activeArt)?.name}
              </span>
            )}
          </span>
          <ChevronDown
            size={13}
            className={`transition-transform ${artistsExpanded ? "rotate-180" : ""}`}
          />
        </button>
        {artistsExpanded && (
          <div className="border border-[var(--border)] bg-[var(--bg)]">
            <div className="p-2 border-b border-[var(--border)]">
              <input
                type="text"
                value={artistSearch}
                onChange={(e) => setArtistSearch(e.target.value)}
                placeholder={t.catalog?.searchArtist || "Sənətçi axtar…"}
                className="w-full bg-transparent outline-none text-xs px-2 py-1"
                data-testid="filter-artist-search"
              />
            </div>
            <ul className="max-h-64 overflow-y-auto divide-y divide-[var(--border)]/60">
              <li>
                <button
                  type="button"
                  onClick={() => { setActiveArt(""); applyFilters({ artist: "" }); }}
                  className={`w-full text-left px-3 py-2 text-sm ${
                    activeArt === "" ? "text-[var(--accent)] font-medium" : "text-[var(--text-2)] hover:text-[var(--text)]"
                  }`}
                  data-testid="filter-art-all"
                >
                  {t.catalog?.all || "Hamısı"}
                </button>
              </li>
              {filteredArtists.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => pickArtist(a.slug)}
                    className={`w-full text-left px-3 py-2 text-sm ${
                      activeArt === a.slug ? "text-[var(--accent)] font-medium" : "text-[var(--text-2)] hover:text-[var(--text)]"
                    }`}
                    data-testid={`filter-art-${a.slug}`}
                  >
                    {a.name}
                  </button>
                </li>
              ))}
              {filteredArtists.length === 0 && (
                <li className="px-3 py-2 text-[11px] text-[var(--text-muted)]">
                  {t.common.empty || "Boşdur"}
                </li>
              )}
            </ul>
          </div>
        )}
      </div>

      {/* Sort */}
      <div>
        <div className="text-[10px] tracking-[0.16em] uppercase text-[var(--text-muted)] mb-2 font-medium">
          {t.catalog?.sort || "Sıralama"}
        </div>
        <select
          value={sort}
          onChange={(e) => { setSort(e.target.value); applyFilters({ sort: e.target.value }); }}
          className="w-full bg-[var(--bg)] border border-[var(--border)] px-3 py-2 outline-none text-sm focus:border-[var(--accent)]"
          data-testid="filter-sort"
        >
          <option value="newest">{t.catalog?.sortNewest || "Ən yenilər"}</option>
          <option value="price_asc">{t.catalog?.sortPriceAsc || "Qiymət: aşağıdan yuxarı"}</option>
          <option value="price_desc">{t.catalog?.sortPriceDesc || "Qiymət: yuxarıdan aşağı"}</option>
          <option value="title_asc">{t.catalog?.sortTitleAsc || "Ad: A–Z"}</option>
        </select>
      </div>

      {/* Clear all */}
      {activeChipsCount > 0 && (
        <button
          type="button"
          onClick={clearAll}
          className="w-full py-2 border border-[var(--border)] text-xs tracking-[0.14em] uppercase text-[var(--text-2)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"
          data-testid="filter-clear-all"
        >
          {t.common.clear || "Filterləri sıfırla"} ({activeChipsCount})
        </button>
      )}
    </div>
  );

  return (
    <div className="max-w-screen-2xl mx-auto px-4 md:px-12 py-10 md:py-16" data-testid="catalog-page">
      <div className="eyebrow mb-3">{tp("catalog", "eyebrow", t.catalog?.archive || "ARXİV")}</div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8 md:mb-10">
        <h1 className="section-title text-4xl md:text-6xl lg:text-7xl">
          {tp("catalog", "title", t.nav.catalog)}
        </h1>
        <div className="metadata">
          {visibleProducts.length} {t.catalog?.releases || "buraxılış"}
        </div>
      </div>

      {/* Mobile filter toggle */}
      <div className="md:hidden mb-4">
        <button
          type="button"
          onClick={() => setFiltersOpen((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 border border-[var(--border)] bg-[var(--bg)] text-sm"
          data-testid="filters-toggle"
        >
          <span className="flex items-center gap-2">
            <SlidersHorizontal size={14} />
            <span className="tracking-[0.14em] uppercase text-[11px]">
              {t.catalog?.filters || "Filterlər"}
            </span>
            {activeChipsCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-[var(--accent)] text-white rounded-full">
                {activeChipsCount}
              </span>
            )}
          </span>
          <ChevronDown size={14} className={`transition-transform ${filtersOpen ? "rotate-180" : ""}`} />
        </button>
      </div>

      {/* Mobile filter drawer (collapsible) */}
      {filtersOpen && (
        <div className="md:hidden mb-6 border border-[var(--border)] bg-[var(--bg)] p-4" data-testid="filters-mobile">
          <div className="flex items-center justify-between mb-4">
            <div className="tracking-[0.14em] uppercase text-[11px] text-[var(--text-muted)]">
              {t.catalog?.filters || "Filterlər"}
            </div>
            <button
              type="button"
              onClick={() => setFiltersOpen(false)}
              className="text-[var(--text-2)] hover:text-[var(--text)]"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
          {renderFilterBlock()}
        </div>
      )}

      {/* Desktop: filters on TOP as a compact horizontal card */}
      <div className="hidden md:block mb-10">
        <div className="border border-[var(--border)] bg-[var(--surface)]/40 p-5 rounded-sm">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-3">
              <FilterBlockSection title={t.common.search}>
                <form onSubmit={onSearch} className="flex border border-[var(--border)] bg-[var(--bg)]">
                  <input
                    type="text" value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder={t.common.search}
                    className="bg-transparent flex-1 px-3 py-2 outline-none text-sm min-w-0"
                    data-testid="catalog-search-input-desktop"
                  />
                  <button type="submit" className="px-3 border-l border-[var(--border)] hover:bg-[var(--surface)] text-[var(--text-2)]">
                    <Search size={14} />
                  </button>
                </form>
              </FilterBlockSection>

              <FilterBlockSection title={t.catalog?.priceRange || "Qiymət aralığı (AZN)"} className="mt-5">
                <div className="flex items-center gap-2">
                  <input
                    type="number" min="0" inputMode="numeric"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    onBlur={() => applyFilters({ min: minPrice })}
                    placeholder="Min"
                    className="w-full bg-[var(--bg)] border border-[var(--border)] px-3 py-2 outline-none text-sm focus:border-[var(--accent)]"
                    data-testid="filter-price-min-desktop"
                  />
                  <span className="text-[var(--text-muted)] text-sm">—</span>
                  <input
                    type="number" min="0" inputMode="numeric"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    onBlur={() => applyFilters({ max: maxPrice })}
                    placeholder="Max"
                    className="w-full bg-[var(--bg)] border border-[var(--border)] px-3 py-2 outline-none text-sm focus:border-[var(--accent)]"
                    data-testid="filter-price-max-desktop"
                  />
                </div>
              </FilterBlockSection>

              <FilterBlockSection title={t.catalog?.sort || "Sıralama"} className="mt-5">
                <select
                  value={sort}
                  onChange={(e) => { setSort(e.target.value); applyFilters({ sort: e.target.value }); }}
                  className="w-full bg-[var(--bg)] border border-[var(--border)] px-3 py-2 outline-none text-sm focus:border-[var(--accent)]"
                >
                  <option value="newest">{t.catalog?.sortNewest || "Ən yenilər"}</option>
                  <option value="price_asc">{t.catalog?.sortPriceAsc || "Qiymət: aşağıdan yuxarı"}</option>
                  <option value="price_desc">{t.catalog?.sortPriceDesc || "Qiymət: yuxarıdan aşağı"}</option>
                  <option value="title_asc">{t.catalog?.sortTitleAsc || "Ad: A–Z"}</option>
                </select>
              </FilterBlockSection>

              {activeChipsCount > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="mt-5 w-full py-2 border border-[var(--border)] text-[11px] tracking-[0.14em] uppercase text-[var(--text-2)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"
                  data-testid="filter-clear-all-desktop"
                >
                  {t.common.clear || "Sıfırla"} ({activeChipsCount})
                </button>
              )}
            </div>

            {/* Categories tree — spans 5 cols */}
            <div className="col-span-5 border-l border-[var(--border)] pl-6">
              <FilterBlockSection
                title={t.catalog?.categoriesFilter || "Kateqoriyalar"}
                right={activeCat && (
                  <button
                    type="button"
                    onClick={() => pickCategory(activeCat)}
                    className="text-[10px] tracking-[0.14em] uppercase text-[var(--accent)] hover:underline"
                  >
                    {t.common.clear || "Sıfırla"}
                  </button>
                )}
              >
                <ul className="border border-[var(--border)] bg-[var(--bg)] max-h-72 overflow-y-auto divide-y divide-[var(--border)]/60">
                  <li>
                    <button
                      type="button"
                      onClick={() => { setActiveCat(""); applyFilters({ category: "" }); }}
                      className={`w-full text-left px-3 py-2 text-sm ${
                        activeCat === "" ? "text-[var(--accent)] font-medium" : "text-[var(--text-2)] hover:text-[var(--text)]"
                      }`}
                    >
                      {t.catalog?.all || "Hamısı"}
                    </button>
                  </li>
                  <li>
                    <ul className="px-1">
                      {categoryTree.map((c) => renderCategoryNode({ node: c, depth: 0, activeCat, expandedCats, pickCategory, toggleCatExpand, setExpandedCats }))}
                      {categoryTree.length === 0 && (
                        <li className="px-3 py-2 text-[11px] text-[var(--text-muted)]">…</li>
                      )}
                    </ul>
                  </li>
                </ul>
              </FilterBlockSection>
            </div>

            {/* Artists — spans 4 cols */}
            <div className="col-span-4 border-l border-[var(--border)] pl-6">
              <FilterBlockSection
                title={t.catalog?.artistsFilter || "Sənətçilər"}
                right={activeArt && (
                  <button
                    type="button"
                    onClick={() => pickArtist(activeArt)}
                    className="text-[10px] tracking-[0.14em] uppercase text-[var(--accent)] hover:underline"
                  >
                    {t.common.clear || "Sıfırla"}
                  </button>
                )}
              >
                <div className="border border-[var(--border)] bg-[var(--bg)]">
                  <div className="p-2 border-b border-[var(--border)]">
                    <input
                      type="text"
                      value={artistSearch}
                      onChange={(e) => setArtistSearch(e.target.value)}
                      placeholder={t.catalog?.searchArtist || "Sənətçi axtar…"}
                      className="w-full bg-transparent outline-none text-xs px-2 py-1"
                    />
                  </div>
                  <ul className="max-h-64 overflow-y-auto divide-y divide-[var(--border)]/60">
                    <li>
                      <button
                        type="button"
                        onClick={() => { setActiveArt(""); applyFilters({ artist: "" }); }}
                        className={`w-full text-left px-3 py-2 text-sm ${
                          activeArt === "" ? "text-[var(--accent)] font-medium" : "text-[var(--text-2)] hover:text-[var(--text)]"
                        }`}
                      >
                        {t.catalog?.all || "Hamısı"}
                      </button>
                    </li>
                    {filteredArtists.map((a) => (
                      <li key={a.id}>
                        <button
                          type="button"
                          onClick={() => pickArtist(a.slug)}
                          className={`w-full text-left px-3 py-2 text-sm ${
                            activeArt === a.slug ? "text-[var(--accent)] font-medium" : "text-[var(--text-2)] hover:text-[var(--text)]"
                          }`}
                        >
                          {a.name}
                        </button>
                      </li>
                    ))}
                    {filteredArtists.length === 0 && (
                      <li className="px-3 py-2 text-[11px] text-[var(--text-muted)]">
                        {t.common.empty || "Boşdur"}
                      </li>
                    )}
                  </ul>
                </div>
              </FilterBlockSection>
            </div>
          </div>
        </div>
      </div>

      {/* Products grid — full width, no left sidebar */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-8" data-testid="catalog-grid">
        {visibleProducts.map((p) => (
          <ProductCard
            key={p.id}
            product={p}
            artistName={artistById[p.artist_id]?.name}
            artistSlug={artistById[p.artist_id]?.slug}
            artistId={p.artist_id}
          />
        ))}
        {visibleProducts.length === 0 && (
          <div className="col-span-full text-[var(--text-2)] py-20 text-center">
            {t.common.empty || "Nəticə tapılmadı"}
          </div>
        )}
      </div>
    </div>
  );
}

function renderCategoryNode({ node, depth = 0, activeCat, expandedCats, pickCategory, toggleCatExpand, setExpandedCats }) {
  const hasChildren = node.children && node.children.length > 0;
  const isActive = activeCat === node.slug;
  const isExpanded = !!expandedCats[node.id];
  return (
    <li key={node.id}>
      <div className="flex items-stretch">
        <button
          type="button"
          onClick={() => {
            pickCategory(node.slug);
            if (hasChildren) setExpandedCats((s) => ({ ...s, [node.id]: true }));
          }}
          className={`flex-1 text-left py-2 pr-2 text-sm transition-colors ${
            isActive ? "text-[var(--accent)] font-medium" : "text-[var(--text-2)] hover:text-[var(--text)]"
          }`}
          style={{ paddingLeft: `${depth * 14}px` }}
          data-testid={`filter-cat-${node.slug}`}
        >
          {node.name}
        </button>
        {hasChildren && (
          <button
            type="button"
            onClick={() => toggleCatExpand(node.id)}
            className="px-2 flex items-center text-[var(--text-muted)] hover:text-[var(--text)]"
            aria-label="Expand"
            data-testid={`filter-cat-toggle-${node.slug}`}
          >
            <ChevronDown
              size={13}
              className={`transition-transform ${isExpanded ? "rotate-180" : ""}`}
            />
          </button>
        )}
      </div>
      {hasChildren && isExpanded && (
        <ul className="border-l border-[var(--border)] ml-2">
          {node.children.map((child) => renderCategoryNode({ node: child, depth: depth + 1, activeCat, expandedCats, pickCategory, toggleCatExpand, setExpandedCats }))}
        </ul>
      )}
    </li>
  );
}

function FilterBlockSection({ title, right, className = "", children }) {
  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] tracking-[0.16em] uppercase text-[var(--text-muted)] font-medium">
          {title}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}
